/**
 * @file GenerateView.js
 * @description Step 3 of the bulk QR wizard: display a summary of what will
 * be generated, kick off the generation, show live progress, and present the
 * success / error state.
 *
 * Design notes:
 *  - Three stat cards (Total / Valid / Skipped) give an at-a-glance sanity check.
 *  - A compact preview table of the first 10 valid rows lets the user spot
 *    mapping mistakes before committing to a long generation run.
 *  - Threshold warnings/errors are shown BEFORE the Generate button so the
 *    user understands the limits before they click.
 *  - The progress section uses a MUI LinearProgress bar with a text overlay so
 *    both a percentage and the raw done/total counts are always visible.
 *  - Elapsed time and ETA are rendered in a monospace style to prevent layout
 *    shift as the digits change.
 *  - The Cancel button is disabled during the 'zipping' phase because aborting
 *    mid-ZIP would produce a corrupt file rather than a clean cancel.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    LinearProgress,
    Chip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ArchiveIcon from '@material-ui/icons/Archive';
import RefreshIcon from '@material-ui/icons/Refresh';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Number of valid rows shown in the preview table. */
const PREVIEW_ROW_COUNT = 10;

/**
 * Row count above which we warn the user about expected generation time.
 * Below 10,000 (the hard cap) but large enough to warrant a heads-up.
 */
const LARGE_BATCH_WARNING_THRESHOLD = 5000;

/** Hard row limit — must match MAX_ROW_LIMIT in fileParser.js. */
const HARD_ROW_LIMIT = 10_000;

/** Rough estimate: ~50 ms per QR at 600×600 on a modern laptop (conservative). */
const MS_PER_QR_ESTIMATE = 50;

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    statCard: {
        padding: theme.spacing(2.5),
        borderRadius: theme.shape.borderRadius * 1.5,
        textAlign: 'center',
        border: `1px solid ${theme.palette.divider}`,
    },
    statValue: {
        fontSize: '2rem',
        fontWeight: 800,
        lineHeight: 1.2,
    },
    statLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.82rem',
        marginTop: theme.spacing(0.5),
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    statCardValid: {
        borderColor: 'rgba(56, 142, 60, 0.3)',
        backgroundColor: 'rgba(56, 142, 60, 0.05)',
        '& $statValue': { color: '#388e3c' },
    },
    statCardSkipped: {
        borderColor: 'rgba(245, 124, 0, 0.3)',
        backgroundColor: 'rgba(245, 124, 0, 0.05)',
        '& $statValue': { color: '#f57c00' },
    },
    statCardTotal: {
        borderColor: 'rgba(67, 65, 158, 0.3)',
        backgroundColor: 'rgba(67, 65, 158, 0.05)',
        '& $statValue': { color: '#43419e' },
    },
    // Preview table
    previewSection: {
        marginTop: theme.spacing(3),
    },
    previewTitle: {
        fontWeight: 700,
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: theme.spacing(1),
    },
    previewPaper: {
        overflowX: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    tableHeadCell: {
        fontWeight: 700,
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[50],
    },
    tableCell: {
        fontSize: '0.82rem',
    },
    // Alert rows
    alertRow: {
        marginTop: theme.spacing(2),
    },
    // Generate button area
    generateArea: {
        marginTop: theme.spacing(3),
        display: 'flex',
        justifyContent: 'center',
    },
    generateButton: {
        backgroundColor: '#43419e',
        color: '#fff',
        fontWeight: 700,
        padding: theme.spacing(1.25, 5),
        fontSize: '1rem',
        '&:hover': {
            backgroundColor: '#302f80',
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
        },
    },
    // Progress section
    progressSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius * 1.5,
    },
    progressHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1.5),
    },
    stageChip: {
        fontWeight: 700,
        fontSize: '0.78rem',
    },
    linearProgressRoot: {
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.palette.grey[200],
    },
    linearProgressBar: {
        borderRadius: 5,
        backgroundColor: '#43419e',
    },
    linearProgressBarDone: {
        borderRadius: 5,
        backgroundColor: '#388e3c',
    },
    progressLabel: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(0.75),
    },
    timeRow: {
        display: 'flex',
        gap: theme.spacing(3),
        marginTop: theme.spacing(1.5),
    },
    monoText: {
        fontFamily: '"Roboto Mono", "Courier New", monospace',
        fontSize: '0.85rem',
        color: theme.palette.text.secondary,
    },
    monoLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginBottom: 2,
    },
    // Success / Error states
    successBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(3),
        border: `1px solid rgba(56, 142, 60, 0.3)`,
        borderRadius: theme.shape.borderRadius * 1.5,
        backgroundColor: 'rgba(56, 142, 60, 0.05)',
        marginTop: theme.spacing(2),
    },
    successIcon: {
        fontSize: 48,
        color: '#388e3c',
        marginBottom: theme.spacing(1),
    },
    successText: {
        fontWeight: 700,
        color: '#388e3c',
        textAlign: 'center',
    },
    cancelButton: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        fontWeight: 700,
        '&:hover': {
            backgroundColor: 'rgba(211, 47, 47, 0.05)',
            borderColor: theme.palette.error.dark,
        },
        '&.Mui-disabled': {
            opacity: 0.45,
        },
    },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Format a millisecond count to mm:ss.
 *
 * @param {number} ms
 * @returns {string}  e.g. "01:45"
 */
function formatMMSS(ms) {
    if (ms == null || ms < 0) return '--:--';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Determine the colour and label for the stage chip.
 *
 * @param {'idle'|'rendering'|'zipping'|'done'|'error'} stage
 * @returns {{ label: string, color: string, icon: React.ReactNode }}
 */
function getStageInfo(stage) {
    switch (stage) {
        case 'rendering':
            return { label: 'Rendering QRs', color: '#43419e', Icon: HourglassEmptyIcon };
        case 'zipping':
            return { label: 'Compressing ZIP', color: '#f57c00', Icon: ArchiveIcon };
        case 'done':
            return { label: 'Complete', color: '#388e3c', Icon: CheckCircleIcon };
        case 'error':
            return { label: 'Cancelled / Error', color: '#d32f2f', Icon: ErrorOutlineIcon };
        default:
            return { label: 'Idle', color: '#9e9e9e', Icon: HourglassEmptyIcon };
    }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * GenerateView renders the final step of the wizard: a summary of what will
 * be generated, a generate button, live progress tracking, and the final
 * success or error state.
 *
 * @param {object}   props
 * @param {number}   props.totalRows      Total rows in the uploaded file
 * @param {number}   props.validCount     Rows that have both EV and Chassis values
 * @param {number}   props.skippedCount   Rows that will be skipped
 * @param {Array<{evNumber:string,chassisNumber:string}>} props.previewRows  Up to 10 valid rows for preview
 * @param {boolean}  props.isRunning      True while generation is in progress
 * @param {object}   props.progress       Progress object from useBulkQRGenerator
 * @param {function} props.onGenerate     Called when the Generate button is clicked
 * @param {function} props.onCancel       Called when the Cancel button is clicked
 * @param {function} props.onRetry        Called when the Retry button is clicked (error state)
 */
function GenerateView({
    totalRows,
    validCount,
    skippedCount,
    previewRows,
    isRunning,
    progress,
    onGenerate,
    onCancel,
    onRetry,
}) {
    const classes = useStyles();

    const { stage, done, total, percent, elapsedMs, etaMs } = progress;

    const showProgress = isRunning || stage === 'done' || stage === 'error';
    const isOverLimit = validCount > HARD_ROW_LIMIT;
    const isLargeBatch = validCount > LARGE_BATCH_WARNING_THRESHOLD && !isOverLimit;

    // Estimated minutes for the large-batch warning
    const estimatedMinutes = Math.ceil(
        (validCount * MS_PER_QR_ESTIMATE) / 1000 / 60,
    );

    const stageInfo = getStageInfo(stage);

    // The Cancel button should be disabled during zipping because interrupting
    // mid-compression produces a partial (corrupt) ZIP
    const canCancel = isRunning && stage !== 'zipping' && stage !== 'done';

    const generateDisabled =
        isRunning || validCount === 0 || isOverLimit;

    return (
        <Box>
            {/* ── Stat cards ────────────────────────────────────────────────── */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <Paper
                        elevation={0}
                        className={`${classes.statCard} ${classes.statCardTotal}`}
                    >
                        <Typography className={classes.statValue}>
                            {totalRows.toLocaleString()}
                        </Typography>
                        <Typography className={classes.statLabel}>Total Rows</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper
                        elevation={0}
                        className={`${classes.statCard} ${classes.statCardValid}`}
                    >
                        <Typography className={classes.statValue}>
                            {validCount.toLocaleString()}
                        </Typography>
                        <Typography className={classes.statLabel}>Valid QRs</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper
                        elevation={0}
                        className={`${classes.statCard} ${classes.statCardSkipped}`}
                    >
                        <Typography className={classes.statValue}>
                            {skippedCount.toLocaleString()}
                        </Typography>
                        <Typography className={classes.statLabel}>Skipped</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* ── Alerts ───────────────────────────────────────────────────── */}
            {isOverLimit && (
                <Alert severity="error" className={classes.alertRow}>
                    <strong>Exceeds 10,000-row limit.</strong> This file contains{' '}
                    {validCount.toLocaleString()} valid rows. Please split your file into
                    batches of 10,000 or fewer and re-upload.
                </Alert>
            )}

            {isLargeBatch && (
                <Alert severity="warning" className={classes.alertRow}>
                    <strong>Large batch detected.</strong> Generating{' '}
                    {validCount.toLocaleString()} QR codes will take approximately{' '}
                    ~{estimatedMinutes} minute{estimatedMinutes !== 1 ? 's' : ''} depending
                    on your device. You can cancel at any time.
                </Alert>
            )}

            {/* ── Preview table of first 10 valid rows ─────────────────────── */}
            {previewRows.length > 0 && (
                <Box className={classes.previewSection}>
                    <Typography className={classes.previewTitle}>
                        Preview — first {Math.min(PREVIEW_ROW_COUNT, previewRows.length)} valid rows
                    </Typography>
                    <Paper className={classes.previewPaper} elevation={0}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.tableHeadCell}>#</TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        EV Number
                                    </TableCell>
                                    <TableCell className={classes.tableHeadCell}>
                                        Chassis Number
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {previewRows.slice(0, PREVIEW_ROW_COUNT).map((row, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell className={classes.tableCell}>
                                            {i + 1}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {row.evNumber}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {row.chassisNumber}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Box>
            )}

            {/* ── Generate button ───────────────────────────────────────────── */}
            {!showProgress && (
                <Box className={classes.generateArea}>
                    <Button
                        variant="contained"
                        className={classes.generateButton}
                        startIcon={<PlayArrowIcon />}
                        onClick={onGenerate}
                        disabled={generateDisabled}
                        size="large"
                    >
                        Generate &amp; Download ZIP
                    </Button>
                </Box>
            )}

            {/* ── Progress section ─────────────────────────────────────────── */}
            {showProgress && (
                <Box className={classes.progressSection}>
                    {/* Header row: stage chip + cancel button */}
                    <Box className={classes.progressHeader}>
                        <Chip
                            label={stageInfo.label}
                            size="small"
                            className={classes.stageChip}
                            icon={<stageInfo.Icon style={{ fontSize: 16 }} />}
                            style={{
                                backgroundColor: `${stageInfo.color}20`,
                                color: stageInfo.color,
                                borderColor: `${stageInfo.color}60`,
                                border: '1px solid',
                            }}
                        />
                        {isRunning && (
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.cancelButton}
                                startIcon={<CancelIcon />}
                                onClick={onCancel}
                                disabled={!canCancel}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>

                    {/* Progress bar */}
                    {stage !== 'done' && stage !== 'error' && (
                        <>
                            <LinearProgress
                                variant="determinate"
                                value={percent}
                                classes={{
                                    root: classes.linearProgressRoot,
                                    bar: classes.linearProgressBar,
                                }}
                            />
                            <Box className={classes.progressLabel}>
                                <Typography variant="caption" color="textSecondary">
                                    {done.toLocaleString()} / {total.toLocaleString()} QR codes
                                </Typography>
                                <Typography
                                    variant="caption"
                                    style={{ fontWeight: 700, color: '#43419e' }}
                                >
                                    {percent}%
                                </Typography>
                            </Box>

                            {/* Elapsed + ETA in monospace to prevent layout shift */}
                            <Box className={classes.timeRow}>
                                <Box>
                                    <Typography className={classes.monoLabel}>
                                        Elapsed
                                    </Typography>
                                    <Typography className={classes.monoText}>
                                        {formatMMSS(elapsedMs)}
                                    </Typography>
                                </Box>
                                {etaMs != null && (
                                    <Box>
                                        <Typography className={classes.monoLabel}>ETA</Typography>
                                        <Typography className={classes.monoText}>
                                            ~{formatMMSS(etaMs)} remaining
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}

                    {/* ── Success state ────────────────────────────────────── */}
                    {stage === 'done' && (
                        <Box className={classes.successBox}>
                            <CheckCircleIcon className={classes.successIcon} />
                            <Typography variant="h6" className={classes.successText}>
                                {done.toLocaleString()} QR code{done !== 1 ? 's' : ''} downloaded!
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginTop: 4 }}
                            >
                                Total time: {formatMMSS(elapsedMs)}
                            </Typography>
                        </Box>
                    )}

                    {/* ── Error / cancelled state ──────────────────────────── */}
                    {stage === 'error' && (
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={onRetry}
                                >
                                    Retry
                                </Button>
                            }
                        >
                            Generation was cancelled or encountered an error. You can retry
                            or start over with new settings.
                        </Alert>
                    )}
                </Box>
            )}
        </Box>
    );
}

GenerateView.propTypes = {
    /** Total number of rows in the uploaded file. */
    totalRows: PropTypes.number.isRequired,
    /** Number of rows that will produce valid QR codes. */
    validCount: PropTypes.number.isRequired,
    /** Number of rows that will be skipped. */
    skippedCount: PropTypes.number.isRequired,
    /** First N valid rows for the preview table, already mapped to {evNumber, chassisNumber}. */
    previewRows: PropTypes.arrayOf(
        PropTypes.shape({
            evNumber: PropTypes.string.isRequired,
            chassisNumber: PropTypes.string.isRequired,
        }),
    ).isRequired,
    /** True while the generator is running. */
    isRunning: PropTypes.bool.isRequired,
    /** Progress object from useBulkQRGenerator. */
    progress: PropTypes.shape({
        done: PropTypes.number,
        total: PropTypes.number,
        percent: PropTypes.number,
        stage: PropTypes.oneOf(['idle', 'rendering', 'zipping', 'done', 'error']),
        elapsedMs: PropTypes.number,
        etaMs: PropTypes.number,
    }).isRequired,
    /** Called when the Generate button is clicked. */
    onGenerate: PropTypes.func.isRequired,
    /** Called when the Cancel button is clicked. */
    onCancel: PropTypes.func.isRequired,
    /** Called when the Retry button is clicked after an error. */
    onRetry: PropTypes.func.isRequired,
};

export default GenerateView;
